import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable, catchError, throwError } from 'rxjs';
import { Microservice } from '@hypenation/shared';
import {
  CustomerDashboardDto,
  CustomerOrderStatsDto,
  CheckInRecord,
  CheckInStats,
  FavouriteOrganiser,
  OrganiserDto,
  OrganiserPublicProfileResponse,
  CustomerFavouriteOrganiser,
} from '../models/client-portal.models';
import { PaginationResponse, Order, EventModel } from '@hypenation/tickets';

@Injectable({
  providedIn: 'root',
})
export class ClientPortalApiService {
  private readonly apiUrl: string = environment.apiUrl;

  constructor(private readonly _http: HttpClient) {}

  public getDashboard(email: string): Observable<HttpResponse<CustomerDashboardDto>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      observe: 'response' as const,
    };

    return this._http
      .get<CustomerDashboardDto>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/client/v1/dashboard?email=${encodeURIComponent(email)}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => throwError(() => error))
      );
  }

  public getOrderStats(email: string): Observable<HttpResponse<CustomerOrderStatsDto>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      observe: 'response' as const,
    };

    return this._http
      .get<CustomerOrderStatsDto>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/client/v1/orders/stats?email=${encodeURIComponent(email)}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => throwError(() => error))
      );
  }

  public getOrdersByUser(
    email: string, 
    page = 0, 
    size = 20
  ): Observable<HttpResponse<PaginationResponse<Order[]>>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      observe: 'response' as const,
    };

    return this._http
      .get<PaginationResponse<Order[]>>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/client/v1/orders/by-user?email=${encodeURIComponent(email)}&page=${page}&size=${size}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => throwError(() => error))
      );
  }

  public getMyCheckins(
    email: string, 
    page = 0, 
    size = 20, 
    sort = 'createdDate,desc'
  ): Observable<HttpResponse<PaginationResponse<CheckInRecord[]>>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      observe: 'response' as const,
    };

    return this._http
      .get<PaginationResponse<CheckInRecord[]>>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/client/v1/checkins/my?customerEmail=${encodeURIComponent(email)}&page=${page}&size=${size}&sort=${sort}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => throwError(() => error))
      );
  }

  public getMyCheckinsStats(email: string): Observable<HttpResponse<CheckInStats>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      observe: 'response' as const,
    };

    return this._http
      .get<CheckInStats>(
        `${this.apiUrl}/${Microservice.ECOMMERCE}/client/v1/checkins/my/stats?customerEmail=${encodeURIComponent(email)}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => throwError(() => error))
      );
  }

  public getMyFavouriteOrganisers(
    email: string, 
    limit = 10
  ): Observable<HttpResponse<FavouriteOrganiser[]>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      observe: 'response' as const,
    };

    return this._http
      .get<FavouriteOrganiser[]>(
        `${this.apiUrl}/${Microservice.ORGANISERS}/client/v1/organisers/favourites?email=${encodeURIComponent(email)}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => throwError(() => error))
      );
  }

  public getComingSoonEvents(
    page = 0, 
    size = 20, 
    sort = 'date,asc'
  ): Observable<HttpResponse<PaginationResponse<EventModel[]>>> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      observe: 'response' as const,
    };

    return this._http
      .get<PaginationResponse<EventModel[]>>(
        `${this.apiUrl}/${Microservice.ETICKETING}/opn/v1/events/coming-soon?page=${page}&size=${size}&sort=${sort}`,
        httpOptions
      )
      .pipe(
        catchError((error: Error) => throwError(() => error))
      );
  }

  // ─── Organiser Endpoints ───

  public getPublicOrganisers(): Observable<HttpResponse<OrganiserDto[]>> {
    const httpOptions = {
      headers: new HttpHeaders({ Accept: 'application/json' }),
      observe: 'response' as const,
    };

    return this._http
      .get<OrganiserDto[]>(
        `${this.apiUrl}/${Microservice.ORGANISERS}/opn/v1/organisers/public`,
        httpOptions
      )
      .pipe(catchError((error: Error) => throwError(() => error)));
  }

  public getOrganiserProfile(uid: string): Observable<HttpResponse<OrganiserPublicProfileResponse>> {
    const httpOptions = {
      headers: new HttpHeaders({ Accept: 'application/json' }),
      observe: 'response' as const,
    };

    return this._http
      .get<OrganiserPublicProfileResponse>(
        `${this.apiUrl}/${Microservice.ORGANISERS}/opn/v1/organisers/${uid}/profile`,
        httpOptions
      )
      .pipe(catchError((error: Error) => throwError(() => error)));
  }

  public getOrganiserEvents(uid: string): Observable<HttpResponse<EventModel[]>> {
    const httpOptions = {
      headers: new HttpHeaders({ Accept: 'application/json' }),
      observe: 'response' as const,
    };

    return this._http
      .get<EventModel[]>(
        `${this.apiUrl}/${Microservice.ORGANISERS}/opn/v1/organisers/${uid}/events`,
        httpOptions
      )
      .pipe(catchError((error: Error) => throwError(() => error)));
  }

  public getMyFavouriteOrganiserProfiles(email: string): Observable<HttpResponse<OrganiserDto[]>> {
    const httpOptions = {
      headers: new HttpHeaders({ Accept: 'application/json' }),
      observe: 'response' as const,
    };

    return this._http
      .get<OrganiserDto[]>(
        `${this.apiUrl}/${Microservice.ORGANISERS}/client/v1/organisers/favourites?email=${encodeURIComponent(email)}`,
        httpOptions
      )
      .pipe(catchError((error: Error) => throwError(() => error)));
  }

  public addFavouriteOrganiser(uid: string, email: string): Observable<HttpResponse<CustomerFavouriteOrganiser>> {
    const httpOptions = {
      headers: new HttpHeaders({ Accept: 'application/json' }),
      observe: 'response' as const,
    };

    return this._http
      .post<CustomerFavouriteOrganiser>(
        `${this.apiUrl}/${Microservice.ORGANISERS}/client/v1/organisers/${uid}/favourite?email=${encodeURIComponent(email)}`,
        null,
        httpOptions
      )
      .pipe(catchError((error: Error) => throwError(() => error)));
  }

  public removeFavouriteOrganiser(uid: string, email: string): Observable<HttpResponse<void>> {
    const httpOptions = {
      headers: new HttpHeaders({ Accept: 'application/json' }),
      observe: 'response' as const,
    };

    return this._http
      .delete<void>(
        `${this.apiUrl}/${Microservice.ORGANISERS}/client/v1/organisers/${uid}/favourite?email=${encodeURIComponent(email)}`,
        httpOptions
      )
      .pipe(catchError((error: Error) => throwError(() => error)));
  }

  public isOrganiserFavourite(uid: string, email: string): Observable<HttpResponse<boolean>> {
    const httpOptions = {
      headers: new HttpHeaders({ Accept: 'application/json' }),
      observe: 'response' as const,
    };

    return this._http
      .get<boolean>(
        `${this.apiUrl}/${Microservice.ORGANISERS}/client/v1/organisers/${uid}/is-favourite?email=${encodeURIComponent(email)}`,
        httpOptions
      )
      .pipe(catchError((error: Error) => throwError(() => error)));
  }
}
